import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  InfoIcon,
  ReceiptTextIcon,
  TruckIcon,
} from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  MatchCandidateSearchDialog,
  type CandidateSide,
} from "@/components/MatchCandidateSearchDialog";
import { SuggestionRow } from "@/components/SuggestionRow";
import { formatCurrency, formatDate, formatDateTime, formatNumber } from "@/lib/format";
import {
  allocationConfidence,
  formatConfidence,
  humanizeEnumValue,
  MATCH_LINE_STATES,
  methodBadge,
  num,
  verdictBadge,
} from "@/lib/matching";

type MatchGroupAdminView = components["schemas"]["MatchGroupAdminView"];
type MatchLineView = components["schemas"]["MatchLineView"];
type MatchAllocationAdminView = components["schemas"]["MatchAllocationAdminView"];
type MatchSuggestionView = components["schemas"]["MatchSuggestionView"];
type MatchDnSuggestionView = components["schemas"]["MatchDnSuggestionView"];

// What the organisation's own reviewer sees, plus the diagnostics they aren't shown (in the expanded row).
// Read-only: nothing here writes, so nothing lands in the customer's audit trail under a Dolfin name.
export function MatchGroupPage() {
  const { matchGroupId } = useParams<{ matchGroupId: string }>();

  // #line-{billLineId} comes from the line list. Read once on mount, so it doesn't fight the browser's anchor handling.
  const [highlightLineId] = useState(() =>
    typeof window === "undefined" ? null : window.location.hash.replace(/^#line-/, "") || null,
  );

  const [group, setGroup] = useState<MatchGroupAdminView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Open the clicked row on arrival: the diagnostics the trip was for live in its expanded half.
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    highlightLineId ? new Set([highlightLineId]) : new Set(),
  );
  // Held here, not per row, so only one panel is open and it survives the row collapsing.
  const [searching, setSearching] = useState<{ line: MatchLineView; side: CandidateSide } | null>(null);

  useEffect(() => {
    if (!matchGroupId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .GET("/admin/match-groups/{id}", { params: { path: { id: matchGroupId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("This match group could not be loaded.");
        } else {
          setGroup(data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("This match group could not be loaded.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [matchGroupId]);

  if (loading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (error || !group) {
    return <p className="text-destructive mx-auto w-full max-w-[100rem] p-8">{error ?? "Not found."}</p>;
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <Header group={group} />
      <EngineFreshness group={group} />
      <Counters group={group} />
      <DocumentCards group={group} />
      <LineTable
        group={group}
        expanded={expanded}
        onToggle={setExpanded}
        highlightLineId={highlightLineId}
        onSearchCandidates={(line, side) => setSearching({ line, side })}
      />
      <MatchCandidateSearchDialog
        open={searching !== null}
        onOpenChange={(open) => !open && setSearching(null)}
        side={searching?.side ?? "po"}
        onSideChange={(side) => setSearching((prev) => (prev ? { ...prev, side } : prev))}
        line={searching?.line ?? null}
        invoiceDate={group.bill?.invoiceDate}
        // Confirmed supplier, falling back to the suggested one: the same coalesce as BuildSuggestionsAsync.
        supplierId={group.bill?.supplierId ?? group.bill?.suggestedSupplierId}
        organisationId={group.organisationId}
        similarityThreshold={group.settings.similarityThreshold}
        autoAcceptThreshold={group.settings.autoAcceptThreshold}
        currency={group.bill?.currency ?? "GBP"}
      />
    </div>
  );
}

function Header({ group }: { group: MatchGroupAdminView }) {
  const bill = group.bill;
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="text-muted-foreground mb-1 text-xs font-semibold tracking-[0.08em] uppercase">
          Accounts payable · Three-way match
        </div>
        <h1 className="text-2xl font-semibold">
          {bill?.invoiceNumber || "Match group"}
          {bill?.supplierName && (
            <span className="text-muted-foreground font-normal">
              {" · "}
              {bill.supplierId ? (
                <Link
                  to={`/suppliers/${bill.supplierId}`}
                  className="hover:text-foreground underline-offset-4 hover:underline"
                >
                  {bill.supplierName}
                </Link>
              ) : (
                bill.supplierName
              )}
            </span>
          )}
        </h1>
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-2 text-xs">
          {bill && (
            <Link to={`/bills/${bill.billId}`}>
              <Badge variant="secondary" className="hover:bg-primary/20">
                Bill {bill.billId.slice(0, 8)}…
              </Badge>
            </Link>
          )}
          <Link to={`/organisations/${group.organisationId}`}>
            <Badge variant="secondary" className="hover:bg-primary/20">
              Org {group.organisationId.slice(0, 8)}…
            </Badge>
          </Link>
          <Link to={`/clients/${group.clientId}`}>
            <Badge variant="secondary" className="hover:bg-primary/20">
              Client {group.clientId.slice(0, 8)}…
            </Badge>
          </Link>
          <span>Created {formatDateTime(group.createdAt)}</span>
          <span>· Updated {formatDateTime(group.updatedAt)}</span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <Badge variant={group.status === "Variance" ? "destructive" : "default"}>{group.status}</Badge>
        {/* Writes are only accepted while the bill is in review. */}
        {bill && <span className="text-muted-foreground text-xs">Bill state: {bill.state}</span>}
      </div>
    </div>
  );
}

// Suggestions here are scored on this request; stored verdicts are a snapshot from the last matching run.
// Escalates only once the group was updated since that run; a stale snapshot is otherwise normal.
function EngineFreshness({ group }: { group: MatchGroupAdminView }) {
  const judged = group.engineJudgedAt;
  const stale = !!judged && new Date(judged).getTime() < new Date(group.updatedAt).getTime();

  return (
    <div
      className={`mb-6 rounded-md border px-3 py-2 text-xs ${
        stale ? "border-destructive/40 bg-destructive/5" : "text-muted-foreground"
      }`}
    >
      {judged ? (
        <>
          <span className="font-medium">Engine last judged this group {formatDateTime(judged)}.</span>{" "}
          Suggestions and confidences below are live — scored on this request. The verdicts stored against
          each allocation are from that run.
          {stale && " The group has changed since, so the two can legitimately disagree."}
        </>
      ) : (
        <span className="font-medium">
          Matching has never projected this group, so there are no stored engine verdicts. Everything below
          was scored on this request.
        </span>
      )}
    </div>
  );
}

function Counters({ group }: { group: MatchGroupAdminView }) {
  const c = group.counters;
  const s = group.settings;
  return (
    <div className="mb-6 space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Stat label="Lines" value={formatNumber(num(c.lines))} />
        <Stat
          label="Reconciled"
          value={`${formatNumber(num(c.reconciled))} / ${formatNumber(num(c.lines))}`}
          hint="Cleanly matched, or their difference accepted. A disputed line is recorded but still blocks the bill."
        />
        {/* Each count is one LineBucket, labelled with that bucket's own name and gloss, as the tabs and badges are. */}
        <Stat
          label={humanizeEnumValue("AutoExact")}
          value={formatNumber(num(c.autoExact))}
          hint={MATCH_LINE_STATES.AutoExact}
        />
        <Stat
          label={humanizeEnumValue("AutoFuzzy")}
          value={formatNumber(num(c.autoFuzzy))}
          hint={MATCH_LINE_STATES.AutoFuzzy}
        />
        <Stat
          label={humanizeEnumValue("Assisted")}
          value={formatNumber(num(c.assisted))}
          hint={MATCH_LINE_STATES.Assisted}
        />
        <Stat
          label={humanizeEnumValue("Manual")}
          value={formatNumber(num(c.manual))}
          hint={`${MATCH_LINE_STATES.Manual} The costliest lines.`}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Stat label="Open" value={formatNumber(num(c.openUnmatched))} tone={num(c.openUnmatched) > 0 ? "warn" : undefined} />
        <Stat label="Accepted as billed" value={formatNumber(num(c.acceptedNoMatch))} />
        <Stat label="Disputed" value={formatNumber(num(c.disputed))} tone={num(c.disputed) > 0 ? "bad" : undefined} />
        <Stat
          label="Unresolved variance"
          value={formatNumber(num(c.unresolvedVariance))}
          tone={num(c.unresolvedVariance) > 0 ? "bad" : undefined}
          hint="What the group's Variance status is derived from, and what blocks the bill."
        />
        <Stat
          label="Lines with suggestions"
          value={formatNumber(num(c.linesWithSuggestions))}
          hint="Unmatched lines the engine has something to offer right now. Scored on this request, not a snapshot."
        />
        <Stat
          label="Best suggestion"
          value={formatConfidence(c.topSuggestionConfidence) ?? "—"}
          hint={`Read against the auto-accept threshold of ${formatConfidence(s.autoAcceptThreshold)}.`}
        />
      </div>
      <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span>Auto-accept ≥ {formatConfidence(s.autoAcceptThreshold)}</span>
        <span>Suggest above {formatConfidence(s.similarityThreshold)}</span>
        <span>Rounding tolerance {formatNumber(num(s.roundingTolerance))}</span>
        <span>PO auto-match {s.poAutoMatchEnabled ? "on" : "off"}</span>
        <span>DN auto-match {s.dnAutoMatchEnabled ? "on" : "off"}</span>
        {/* Thresholds are client-scoped and mutable: they describe the engine now, not necessarily the one that made these matches. */}
        <span className="italic">thresholds as they stand now</span>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "warn" | "bad";
}) {
  const toneClass = tone === "bad" ? "text-destructive" : tone === "warn" ? "text-amber-600" : "";
  return (
    <Card className="py-3" title={hint}>
      <CardContent className="px-4">
        <div className="text-muted-foreground text-[11px] tracking-wide uppercase">{label}</div>
        <div className={`mt-0.5 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

// Counts are derived from the allocations: there's no header-level bill-to-PO link to read.
function DocumentCards({ group }: { group: MatchGroupAdminView }) {
  const bill = group.bill;
  const currency = bill?.currency ?? "GBP";

  const { poDocs, dnDocs, billedQty, receivedQty } = useMemo(() => {
    const pos = new Map<string, MatchedDocument>();
    const dns = new Map<string, MatchedDocument>();
    for (const a of group.allocations) {
      if (a.purchaseOrderId) {
        pos.set(
          a.purchaseOrderId,
          accumulate(pos.get(a.purchaseOrderId), a.purchaseOrderId, a.poNumber, a),
        );
      }
      if (a.deliveryNoteId) {
        dns.set(
          a.deliveryNoteId,
          accumulate(dns.get(a.deliveryNoteId), a.deliveryNoteId, a.dnNumber, a),
        );
      }
    }
    return {
      poDocs: [...pos.values()],
      dnDocs: [...dns.values()],
      billedQty: group.view.lines.reduce((n, l) => n + num(l.billedQuantity), 0),
      receivedQty: group.view.lines.reduce((n, l) => n + num(l.receivedQuantity), 0),
    };
  }, [group]);

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
      <DocCard
        icon={<FileTextIcon className="size-5" />}
        eyebrow="Purchase order"
        title={
          poDocs.length === 0 ? "None matched" : `${poDocs.length} order${poDocs.length === 1 ? "" : "s"}`
        }
        footerLabel="Net allocated"
        footerValue={formatCurrency(
          group.view.lines.reduce((n, l) => n + num(l.allocatedNet), 0),
          currency,
        )}
        expandLabel={poDocs.length === 1 ? "order" : "orders"}
        documents={poDocs.map((d) => ({
          id: d.id,
          number: d.number ?? `${d.id.slice(0, 8)}…`,
          href: `/purchase-orders/${d.id}`,
          detail: `${d.lines.size} line${d.lines.size === 1 ? "" : "s"} · ${formatCurrency(d.amount, currency)}`,
        }))}
      />
      <DocCard
        icon={<ReceiptTextIcon className="size-5" />}
        eyebrow="Supplier bill"
        title={bill?.invoiceNumber || "Bill"}
        footerLabel="Billed net"
        footerValue={formatCurrency(num(bill?.subTotal ?? 0), currency)}
        meta={bill?.dueDate ? `Due ${formatDate(bill.dueDate)}` : undefined}
      />
      <DocCard
        icon={<TruckIcon className="size-5" />}
        eyebrow="Delivery note"
        title={
          dnDocs.length === 0
            ? "Not received"
            : `${dnDocs.length} deliver${dnDocs.length === 1 ? "y" : "ies"}`
        }
        footerLabel="Units received"
        footerValue={`${formatNumber(receivedQty)} / ${formatNumber(billedQty)}`}
        expandLabel={dnDocs.length === 1 ? "delivery" : "deliveries"}
        documents={dnDocs.map((d) => ({
          id: d.id,
          number: d.number ?? `${d.id.slice(0, 8)}…`,
          href: `/delivery-notes/${d.id}`,
          detail: `${d.lines.size} line${d.lines.size === 1 ? "" : "s"} · ${formatNumber(d.quantity)} received`,
        }))}
      />
    </div>
  );
}

// A document as the allocations describe it: which bill lines point at it, and how much they carry.
interface MatchedDocument {
  id: string;
  number: string | null;
  lines: Set<string>;
  amount: number;
  quantity: number;
}

function accumulate(
  existing: MatchedDocument | undefined,
  id: string,
  number: string | null | undefined,
  allocation: MatchAllocationAdminView,
): MatchedDocument {
  const doc = existing ?? { id, number: number ?? null, lines: new Set<string>(), amount: 0, quantity: 0 };
  doc.number ??= number ?? null;
  doc.lines.add(allocation.billLineId);
  doc.amount += num(allocation.matchedAmount);
  doc.quantity += num(allocation.matchedQuantity);
  return doc;
}

function DocCard({
  icon,
  eyebrow,
  title,
  footerLabel,
  footerValue,
  meta,
  documents,
  expandLabel,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  footerLabel: string;
  footerValue: string;
  meta?: string;
  documents?: { id: string; number: string; href: string; detail: string }[];
  expandLabel?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="py-4">
      <CardContent className="px-5">
        <div className="text-muted-foreground flex items-center gap-2 text-[11px] font-semibold tracking-wide uppercase">
          {icon}
          {eyebrow}
        </div>
        <div className="mt-2 text-lg font-semibold">{title}</div>
        <div className="text-muted-foreground mt-2 flex items-baseline justify-between text-xs">
          <span>{footerLabel}</span>
          <span className="text-foreground font-medium tabular-nums">{footerValue}</span>
        </div>
        {meta && <div className="text-muted-foreground mt-1 text-xs">{meta}</div>}

        {documents && documents.length > 0 && (
          <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
            <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs font-medium">
              {open ? (
                <ChevronDownIcon className="size-3.5" />
              ) : (
                <ChevronRightIcon className="size-3.5" />
              )}
              {open ? "Hide" : "Show"} {expandLabel}
            </CollapsibleTrigger>
            <CollapsibleContent className="divide-border/60 mt-2 divide-y border-t">
              {documents.map((d) => (
                <div key={d.id} className="flex items-baseline justify-between gap-3 py-1.5">
                  <Link to={d.href} className="font-mono text-xs hover:underline">
                    {d.number}
                  </Link>
                  <span className="text-muted-foreground text-xs tabular-nums">{d.detail}</span>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}

function LineTable({
  group,
  expanded,
  onToggle,
  highlightLineId,
  onSearchCandidates,
}: {
  group: MatchGroupAdminView;
  expanded: Set<string>;
  onToggle: (next: Set<string>) => void;
  highlightLineId: string | null;
  onSearchCandidates: (line: MatchLineView, side: CandidateSide) => void;
}) {
  const currency = group.bill?.currency ?? "GBP";
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [group]);

  // Allocations come as a separate admin projection so they can carry engine diagnostics the tenant view omits.
  const poSuggestions = useMemo(() => groupBy(group.view.suggestions, (s) => s.billLineId), [group]);
  const dnSuggestions = useMemo(() => groupBy(group.view.dnSuggestions, (s) => s.billLineId), [group]);
  const allocations = useMemo(() => groupBy(group.allocations, (a) => a.billLineId), [group]);

  // The unit price only exists on the tenant view of the same allocation; indexed to print "12 × £4.50".
  const orderedUnitPrices = useMemo(
    () =>
      new Map(
        group.view.lines.flatMap((l) =>
          l.allocations.map((a) => [a.id, a.orderedUnitPrice] as const),
        ),
      ),
    [group],
  );

  function toggle(billLineId: string) {
    const next = new Set(expanded);
    if (!next.delete(billLineId)) next.add(billLineId);
    onToggle(next);
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Line</TableHead>
            <TableHead className="text-right">Billed</TableHead>
            <TableHead className="text-right">Allocated</TableHead>
            <TableHead className="text-right">Price var.</TableHead>
            {/* Two quantity columns: over the order and over the delivery are different problems that clear differently. */}
            <TableHead className="text-right">
              <span className="inline-flex items-center gap-1">
                Billed vs ordered
                <InfoHint label="Billed vs ordered" side="bottom">
                  The quantity billed on this line against the quantity ordered across the matched
                  purchase-order lines. The billed figure is always the first one. Red means the gap between
                  them is outside tolerance. Blank when the line has no purchase-order match.
                </InfoHint>
              </span>
            </TableHead>
            <TableHead className="text-right">
              <span className="inline-flex items-center gap-1">
                Billed vs received
                <InfoHint label="Billed vs received" side="bottom">
                  The quantity billed on this line against the quantity received across the matched
                  delivery-note lines. The billed figure is always the first one. Red means the gap between
                  them is outside tolerance. Blank when the line has no delivery-note match.
                </InfoHint>
              </span>
            </TableHead>
            <TableHead>State</TableHead>
            {/* On every row, matched or not: a wrong match needs explaining as much as a missing one. */}
            <TableHead className="text-right">Candidates</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.view.lines.map((line) => {
            const isOpen = expanded.has(line.billLineId);
            const lineAllocations = allocations.get(line.billLineId) ?? [];
            const po = poSuggestions.get(line.billLineId) ?? [];
            const dn = dnSuggestions.get(line.billLineId) ?? [];

            return (
              <Fragment key={line.billLineId}>
                <TableRow
                  id={`line-${line.billLineId}`}
                  // Ring and scroll to the line the reader was sent to.
                  ref={line.billLineId === highlightLineId ? highlightRef : undefined}
                  className={`cursor-pointer ${
                    line.billLineId === highlightLineId
                      ? "bg-amber-500/10 ring-1 ring-amber-500/40 ring-inset"
                      : ""
                  }`}
                  onClick={() => toggle(line.billLineId)}
                >
                  <TableCell>
                    {isOpen ? (
                      <ChevronDownIcon className="size-4" />
                    ) : (
                      <ChevronRightIcon className="size-4" />
                    )}
                  </TableCell>
                  <TableCell className="max-w-80 font-medium">
                    {line.description}
                    <span className="text-muted-foreground mt-0.5 block text-xs">
                      {lineAllocations.length} counterpart{lineAllocations.length === 1 ? "" : "s"}
                      {po.length + dn.length > 0 && ` · ${po.length + dn.length} suggestion${po.length + dn.length === 1 ? "" : "s"}`}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(num(line.billedQuantity))} × {formatCurrency(num(line.billedUnitPrice), currency)}
                    <span className="text-muted-foreground block text-xs">
                      {formatCurrency(num(line.billedNet), currency)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(num(line.allocatedNet), currency)}
                    {!line.fullyAllocated && (
                      <span className="text-muted-foreground block text-xs">
                        {formatCurrency(num(line.unallocatedNet), currency)} left
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Variance
                      value={line.priceVarianceTotal}
                      flagged={line.hasPriceVariance}
                      currency={currency}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <QtyComparison
                      billed={line.billedQuantity}
                      counterpart={line.orderedQuantity}
                      flagged={line.hasOverBillingVariance}
                      noun="ordered"
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <QtyComparison
                      billed={line.billedQuantity}
                      counterpart={line.receivedQuantity}
                      flagged={line.hasQuantityVariance}
                      noun="received"
                    />
                  </TableCell>
                  <TableCell>
                    <LineState line={line} />
                  </TableCell>
                  {/* Stops the row toggling: the reader asked for the search, not for the expander. */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => onSearchCandidates(line, "po")}
                      >
                        PO
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => onSearchCandidates(line, "dn")}
                      >
                        DN
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={9} className="bg-muted/30">
                      <LineDetail
                        allocations={lineAllocations}
                        orderedUnitPrices={orderedUnitPrices}
                        poSuggestions={po}
                        dnSuggestions={dn}
                        autoAcceptThreshold={group.settings.autoAcceptThreshold}
                        currency={currency}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function Variance({
  value,
  flagged,
  currency,
}: {
  value?: number | string | null;
  flagged: boolean;
  currency?: string;
}) {
  // Null means no counterpart on that dimension, not a zero variance.
  if (value === null || value === undefined) return <span className="text-muted-foreground">-</span>;
  const text = currency ? formatCurrency(num(value), currency) : formatNumber(num(value));
  return <span className={flagged ? "text-destructive font-medium" : ""}>{text}</span>;
}

// Both quantities, never their difference: "52 vs 53 ordered" reads on sight; "of" when they agree.
function QtyComparison({
  billed,
  counterpart,
  flagged,
  noun,
}: {
  billed: number | string;
  counterpart?: number | string | null;
  flagged: boolean;
  noun: string;
}) {
  // Null means no counterpart on that dimension, not an exact match.
  if (counterpart === null || counterpart === undefined) {
    return <span className="text-muted-foreground">-</span>;
  }
  const billedQty = num(billed);
  const counterpartQty = num(counterpart);
  return (
    <span className={flagged ? "text-destructive font-medium" : ""}>
      {formatNumber(billedQty)} {billedQty === counterpartQty ? "of" : "vs"} {formatNumber(counterpartQty)}
      <span className="text-muted-foreground ml-1 text-xs">{noun}</span>
    </span>
  );
}

function LineState({ line }: { line: MatchLineView }) {
  if (line.resolution) {
    const blocking =
      line.resolution.action === "Dispute" || line.resolution.action === "RequestCreditNote";
    return (
      <Badge
        variant={blocking ? "destructive" : "default"}
        title={line.resolution.notes ?? undefined}
      >
        {line.resolution.action}
      </Badge>
    );
  }
  if (line.hasPriceVariance || line.hasQuantityVariance || line.hasOverBillingVariance) {
    return <Badge variant="destructive">Needs a decision</Badge>;
  }
  if (line.allocations.length === 0) return <Badge variant="outline">Unmatched</Badge>;
  if (!line.fullyAllocated) return <Badge variant="outline">Part-matched</Badge>;
  return <Badge variant="secondary">Clean</Badge>;
}

// The date is a scoring term, so it sits on the row. "no date" rather than "N/A": an undated document scored on name alone.
function suggestionDate(value: string | null | undefined) {
  return value ? formatDate(value) : "no date";
}

// What was matched, and what was offered and not taken (the only place the engine's scores are visible).
function LineDetail({
  allocations,
  orderedUnitPrices,
  poSuggestions,
  dnSuggestions,
  autoAcceptThreshold,
  currency,
}: {
  allocations: MatchAllocationAdminView[];
  orderedUnitPrices: Map<string, number | string | null | undefined>;
  poSuggestions: MatchSuggestionView[];
  dnSuggestions: MatchDnSuggestionView[];
  autoAcceptThreshold: number | string;
  currency: string;
}) {
  return (
    <div className="grid gap-6 py-2 lg:grid-cols-2">
      <div>
        <h3 className="text-muted-foreground mb-2 flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
          Matched
          <InfoHint label="Matched">
            The purple percentage is the raw score the engine gave the counterpart this allocation pins, so
            it is directly comparable to the auto-accept threshold - a row below the bar is a row the engine
            would not have taken on its own. The verdict badge beside it says why it did not. The "i" at the
            end of each row carries when the match was made and by whom.
          </InfoHint>
        </h3>
        {allocations.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing matched against this line yet.</p>
        ) : (
          <div className="divide-border/60 divide-y">
            {allocations.map((a) => (
              <AllocationRow
                key={a.id}
                allocation={a}
                orderedUnitPrice={orderedUnitPrices.get(a.id)}
                currency={currency}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-muted-foreground mb-2 text-[11px] font-semibold tracking-wide uppercase">
          Suggested · not taken
        </h3>
        {poSuggestions.length + dnSuggestions.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            The engine has nothing to offer this line right now.
          </p>
        ) : (
          <div className="space-y-1.5">
            {poSuggestions.map((s) => (
              <SuggestionRow
                key={`po-${s.poLineId}`}
                kind="PO"
                href={`/purchase-orders/${s.poId}`}
                number={s.poNumber}
                description={s.poLineDescription}
                detail={`${suggestionDate(s.poOrderDate)} · ${formatNumber(num(s.quantityOrdered))} @ ${formatCurrency(num(s.orderedUnitPrice), currency)}`}
                confidence={s.confidence}
                boosted={!!s.manualLinkBoosted}
                threshold={autoAcceptThreshold}
              />
            ))}
            {dnSuggestions.map((s) => (
              <SuggestionRow
                key={`dn-${s.dnLineId}`}
                kind="DN"
                href={`/delivery-notes/${s.dnId}`}
                number={s.dnNumber}
                description={s.dnLineDescription}
                detail={`${suggestionDate(s.dnReceivedAt)} · ${formatNumber(num(s.quantityReceived))} received`}
                confidence={s.confidence}
                boosted={!!s.manualLinkBoosted}
                threshold={autoAcceptThreshold}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AllocationRow({
  allocation,
  orderedUnitPrice,
  currency,
}: {
  allocation: MatchAllocationAdminView;
  orderedUnitPrice?: number | string | null;
  currency: string;
}) {
  const badge = methodBadge(allocation);
  const isPo = !!allocation.purchaseOrderId;
  const verdict = verdictBadge(allocation.engineVerdict);
  const displayed = formatConfidence(allocationConfidence(allocation));

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1.5">
      <span className="text-muted-foreground w-6 shrink-0 text-[10px] font-semibold uppercase">
        {isPo ? "PO" : "DN"}
      </span>
      <Link
        to={isPo ? `/purchase-orders/${allocation.purchaseOrderId}` : `/delivery-notes/${allocation.deliveryNoteId}`}
        className="font-mono text-sm hover:underline"
      >
        {(isPo ? allocation.poNumber : allocation.dnNumber) || "unknown"}
      </Link>
      <span className="text-muted-foreground text-sm">
        {isPo ? allocation.poLineDescription : allocation.dnLineDescription}
      </span>
      {/* Quantity × matched price, not a total, so the price can be checked against the order. Delivery notes
          price nothing, so their rows show what arrived. */}
      <span className="text-xs tabular-nums">
        {isPo ? (
          orderedUnitPrice === null || orderedUnitPrice === undefined ? (
            <>
              {formatNumber(num(allocation.matchedQuantity))} ·{" "}
              {formatCurrency(num(allocation.matchedAmount), currency)}
            </>
          ) : (
            <>
              {formatNumber(num(allocation.matchedQuantity))} ×{" "}
              {formatCurrency(num(orderedUnitPrice), currency)}
            </>
          )
        ) : (
          <>
            {formatNumber(num(allocation.matchedQuantity))}{" "}
            <span className="text-muted-foreground">received</span>
          </>
        )}
      </span>
      <Badge variant={badge.variant} className="text-[10px]">
        {badge.label}
      </Badge>
      {/* The allocation's own raw confidence, comparable to the threshold; the verdict badge carries only the
          reason. Exact SKU matches show 100% (see allocationConfidence). */}
      {displayed && (
        <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-purple-700 dark:bg-purple-400/15 dark:text-purple-300">
          {displayed}
        </span>
      )}
      {verdict && (
        <Badge variant={verdict.variant} className="text-[10px]" title={verdict.hint}>
          {verdict.label}
        </Badge>
      )}
      <AllocationProvenance allocation={allocation} currency={currency} />
    </div>
  );
}

// When the match was made, and by whom: stored facts, so they stay true when the live and frozen halves disagree.
// A popover, not InfoHint, because the actor is a link (as in AuditCell).
function AllocationProvenance({
  allocation,
  currency,
}: {
  allocation: MatchAllocationAdminView;
  currency: string;
}) {
  // Created and updated are stamped together on insert; a second's slack keeps "Edited" for real edits.
  const edited =
    new Date(allocation.updatedAt).getTime() - new Date(allocation.createdAt).getTime() > 1000;
  const runnerUp = formatConfidence(allocation.engineRunnerUpConfidence);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="More about this allocation"
          // The row is inside a table row that toggles on click, so the trigger must not bubble.
          onClick={(e) => e.stopPropagation()}
          // cursor-pointer, not cursor-help: a popover opens on click, not hover.
          className="text-muted-foreground/60 hover:text-foreground focus-visible:ring-ring/50 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <InfoIcon className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2 text-xs">
          <ProvenanceRow label="Matched">
            {formatDateTime(allocation.createdAt)}
            <span className="text-muted-foreground mt-0.5 flex items-center gap-1.5">
              by <ActorBadge actorId={allocation.createdBy} />
            </span>
          </ProvenanceRow>

          {/* Only when edited: an in-place edit resets AutoAccepted, so the badges mean something different after. */}
          {edited && (
            <ProvenanceRow label="Edited">
              {formatDateTime(allocation.updatedAt)}
              <span className="text-muted-foreground mt-0.5 flex items-center gap-1.5">
                by <ActorBadge actorId={allocation.updatedBy} />
              </span>
            </ProvenanceRow>
          )}

          {/* The row shows quantity × ordered price; this is the net the allocation carries, which shows how a split line divided. */}
          <ProvenanceRow label="Net matched">
            {formatCurrency(num(allocation.matchedAmount), currency)}
          </ProvenanceRow>

          {runnerUp && <ProvenanceRow label="Runner-up scored">{runnerUp}</ProvenanceRow>}

          {/* This allocation's verdict snapshot time, not the group's: rows re-judged at different times differ. */}
          {allocation.engineVerdictComputedAt && (
            <ProvenanceRow label="Engine's position taken">
              {formatDateTime(allocation.engineVerdictComputedAt)}
            </ProvenanceRow>
          )}

          {/* In full: this gets pasted into log queries. */}
          <ProvenanceRow label="Allocation id">
            <span className="font-mono break-all select-all">{allocation.id}</span>
          </ProvenanceRow>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ProvenanceRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
        {label}
      </div>
      <div className="tabular-nums">{children}</div>
    </div>
  );
}

// A null actor is the engine: the audit interceptor stamps CreatedBy on insert, so no user means none was there.
function ActorBadge({ actorId }: { actorId?: string | null }) {
  if (!actorId) return <span className="text-muted-foreground italic">the engine</span>;

  return (
    <Link to={`/users/${actorId}`} onClick={(e) => e.stopPropagation()}>
      <Badge variant="secondary" className="hover:bg-primary/20 cursor-pointer font-mono text-[10px]">
        {actorId.slice(0, 8)}…
      </Badge>
    </Link>
  );
}

function groupBy<T>(items: readonly T[], key: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = map.get(k);
    if (bucket) bucket.push(item);
    else map.set(k, [item]);
  }
  return map;
}
